import { useMemo } from 'react';

import type { SymbolKey } from '@/entities/symbol';
import type { ExportSettingsType } from '@/features/DataHub/modules/Export/types/context.types';
import { getFromToValues } from '@/features/DataHub/modules/Export/utils';

import { exportRequestService } from '../services';
import { useLatestApiKey } from './useLatestApiKey';

type UseExportApiStringsParams = {
  symbols: SymbolKey[];
  exportSettings: ExportSettingsType;
  userTimezone: string;
  gxURL: string;
};

const useExportApiStrings = ({
  symbols,
  exportSettings,
  userTimezone,
  gxURL
}: UseExportApiStringsParams) => {
  const apiKey = useLatestApiKey();
  const { fromValue, toValue } = getFromToValues(exportSettings);

  const exportApiRequestString = useMemo(() => {
    const baseUrl = exportRequestService.buildApiRequestString({
      gxURL,
      symbols,
      fromValue,
      toValue: toValue || null,
      includeMetadata: exportSettings.includeMetadata,
      userTimezone
    });

    if (!apiKey) return baseUrl;

    try {
      const url = new URL(baseUrl);
      url.searchParams.set('token', apiKey);
      return url.toString();
    } catch {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}token=${apiKey}`;
    }
  }, [symbols, fromValue, toValue, exportSettings.includeMetadata, userTimezone, apiKey, gxURL]);

  const exportCurlRequestString = useMemo(
    () =>
      exportRequestService.buildCurlRequestString({
        gxURL,
        symbols,
        fromValue,
        toValue: toValue || null,
        includeMetadata: exportSettings.includeMetadata,
        userTimezone,
        apiKey: apiKey ?? ''
      }),
    [symbols, fromValue, toValue, exportSettings.includeMetadata, userTimezone, apiKey, gxURL]
  );

  return { exportApiRequestString, exportCurlRequestString };
};

export default useExportApiStrings;
